import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isDictation, type LiveSession } from '@/features/audioRecording';

import { LiveSessionNote } from './LiveSessionNote';
import styles from './RecordingWorkspace.module.scss';
import { TranscriptChunks } from './TranscriptView';
import { WorkspaceLayout } from './WorkspaceLayout';

type Props = {
  session: LiveSession;
  // The recording's controls, the corner window's own: timer, level, pause,
  // stop.
  controls: ReactNode;
  className?: string;
};

// The recording's page while it records, laid out like the start page it was
// started from: the live note, with the recording and its live transcript on
// the right.
export const RecordingWorkspace: FC<Props> = ({
  session,
  controls,
  className,
}) => {
  const { formatMessage } = useIntl();
  const { live } = session;
  const chunks = live?.chunks ?? [];
  const partial = live?.partial ?? '';

  return (
    <WorkspaceLayout
      className={className}
      recordingPanel={
        <>
          {controls}
          <section
            className={styles.transcript}
            data-component="workspace-live-transcript"
          >
            <span className={cn('p4', styles.label)}>
              <FormattedMessage
                id="workspace.live.transcriptTitle"
                defaultMessage="Live transcript"
              />
            </span>
            {chunks.length || partial ? (
              <TranscriptChunks
                chunks={chunks}
                partial={partial}
                speaker={formatMessage({
                  id: 'workspace.transcript.clinician',
                  defaultMessage: 'Clinician',
                })}
              />
            ) : (
              <p className={cn('p3', styles.pending)}>
                {isDictation(live) ? (
                  <FormattedMessage
                    id="workspace.live.transcriptPending"
                    defaultMessage="The transcript appears here as you dictate."
                  />
                ) : (
                  <FormattedMessage
                    id="workspace.live.transcriptAfterStop"
                    defaultMessage="The transcript appears after the recording is processed."
                  />
                )}
              </p>
            )}
          </section>
        </>
      }
      documents={<LiveSessionNote session={session} />}
    />
  );
};
